'use client';

import {Toggle as TogglePrimitive} from '@base-ui/react/toggle';
import {ToggleGroup as ToggleGroupPrimitive} from '@base-ui/react/toggle-group';
import {cva} from 'class-variance-authority';

import {cn} from '../../lib/utils';

import type {VariantProps} from 'class-variance-authority';

const buttonGroupItemVariants = cva(
  [
    'relative inline-flex cursor-pointer select-none items-center justify-center',
    'whitespace-nowrap transition-all',
    'outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring',
    'disabled:pointer-events-none disabled:cursor-not-allowed disabled:text-fg-disabled'
  ].join(' '),
  {
    variants: {
      variant: {
        default: [
          'gap-2 rounded-lg font-semibold rtl:flex-row-reverse',
          'text-fg-tertiary hover:text-fg-secondary',
          'data-[pressed]:bg-background-surface data-[pressed]:text-fg',
          'data-[pressed]:shadow-xs data-[pressed]:ring-1 data-[pressed]:ring-inset data-[pressed]:ring-border'
        ].join(' '),
        pill: [
          'rounded-sm border border-border bg-background-surface py-1',
          'text-sm font-medium text-fg-secondary',
          'hover:border-fg-tertiary',
          'data-[pressed]:border-brand-accent data-[pressed]:font-semibold data-[pressed]:text-fg data-[pressed]:shadow-sm',
          'disabled:opacity-50'
        ].join(' '),
        divided: [
          'min-h-10 gap-0 rounded-none text-sm font-semibold text-fg-secondary rtl:flex-row-reverse',
          'border-e border-border bg-background-tertiary last:border-e-0',
          'data-[pressed]:bg-background-surface',
          'data-[pressed]:shadow-[0px_1px_2px_0px_rgba(10,13,18,0.05),inset_0px_0px_0px_1px_rgba(10,13,18,0.18),inset_0px_-2px_0px_0px_rgba(10,13,18,0.05)]'
        ].join(' ')
      },
      size: {
        default: 'h-9 text-sm',
        sm: 'h-7 gap-1.5 text-xs',
        icon: 'h-9'
      }
    },
    compoundVariants: [
      {variant: 'default', size: 'default', className: 'px-[18px]'},
      {variant: 'default', size: 'sm', className: 'px-2.5'},
      {variant: 'default', size: 'icon', className: 'w-9 px-2'},
      {variant: 'pill', className: 'px-[10px]'},
      {variant: 'divided', className: 'px-4 py-2'}
    ],
    defaultVariants: {
      variant: 'default',
      size: 'default'
    }
  }
);

function ButtonGroup({className, ...props}: ToggleGroupPrimitive.Props) {
  return (
    <ToggleGroupPrimitive
      data-slot="button-group"
      className={cn(
        'inline-flex items-center gap-1 rounded-[10px] border border-border-subtle bg-background-secondary p-[5px]',
        'aria-invalid:border-destructive aria-invalid:ring-3 aria-invalid:ring-destructive/20',
        className
      )}
      {...props}
    />
  );
}

function ButtonGroupItem({
  className,
  size,
  variant,
  ...props
}: TogglePrimitive.Props & VariantProps<typeof buttonGroupItemVariants>) {
  return (
    <TogglePrimitive
      data-slot="button-group-item"
      className={cn(buttonGroupItemVariants({variant, size}), className)}
      {...props}
    />
  );
}

export {ButtonGroup, ButtonGroupItem};
